/*
 * Copyright Mimic Networks, Inc. 2025.
 */

import { PlusOutlined } from '@ant-design/icons';
import { useState } from 'react';
import { useNavigate } from 'react-router-dom';

import { ContentContainer } from '@/components/ContentContainer';
import { GuardedButton } from '@/components/GuardedButton';
import { PageHeader } from '@/components/PageHeader';
import { QueryWrapper } from '@/components/QueryWrapper';
import { usePaginatedOAuthClientsQuery } from '@/hooks/query/usePaginatedOAuthClientsQuery';
import { useMimicTranslation } from '@/hooks/useMimicTranslation';
import { DeleteOAuthClientModal } from '@/pages/OAuthClients/DeleteOAuthClientModal';
import { OAuthClientsTable } from '@/pages/OAuthClients/OAuthClientsTable';
import { Container } from '@/primitives/Container';
import { useMgmtPlaneStateStore } from '@/state/mgmtPlaneStore';

export function OAuthClientsPage() {
  const { t } = useMimicTranslation('oauthClients');
  const navigate = useNavigate();
  const tenantID = useMgmtPlaneStateStore((state) => state.selectedTenantID);
  const [clientToDelete, setClientToDelete] = useState<{ id: string; name: string } | undefined>(undefined);

  const oauthClientsQuery = usePaginatedOAuthClientsQuery({
    type: 'queryParams',
  });

  if (!tenantID) return null;

  const deleteOAuthClient = (oauthClientId: string, oauthClientName: string) => {
    setClientToDelete({ id: oauthClientId, name: oauthClientName });
  };

  const closeDeleteDialog = () => {
    setClientToDelete(undefined);
  };

  const navigateToCreateClient = () => {
    navigate(`/tenants/${tenantID}/oauth-clients/new`);
  };

  const primaryAction = (
    <GuardedButton
      requiredRole="admin"
      type="primary"
      dataTestId="create-oauth-client"
      onClick={navigateToCreateClient}
    >
      <PlusOutlined /> {t('newOAuthClient')}
    </GuardedButton>
  );

  return (
    <ContentContainer width="100%">
      <Container full style={{ width: '100%' }}>
        <PageHeader title={t('listTitle')} level={2} />
        <QueryWrapper queryResult={oauthClientsQuery}>
          {(oauthClientsData) => (
            <OAuthClientsTable
              oauthClientsData={oauthClientsData}
              updateQueryParams={oauthClientsQuery.updateQueryParams}
              oauthClientFilters={oauthClientsQuery.filters}
              deleteOAuthClient={deleteOAuthClient}
              primaryAction={primaryAction}
            />
          )}
        </QueryWrapper>

        {clientToDelete && (
          <DeleteOAuthClientModal
            oauthClientId={clientToDelete.id}
            oauthClientName={clientToDelete.name}
            onCancelDeletion={closeDeleteDialog}
          />
        )}
      </Container>
    </ContentContainer>
  );
}
